import React from "react";
import { User } from "../api";

export interface AnalyticsContextValue {
  maybeStartAnalytics: () => void;
  identifyUser: (user: User) => void;
  resetUser: (user: User) => void;
  logEvent: (AnalyticsEvent) => void;
  setIncludeIP: (includeIP: boolean) => void;
  registerSuperProperties: (properties: AnalyticsProperties) => void;
  superPropertiesFromUser: (user: User) => AnalyticsProperties;
}

export type AnalyticsProperties = { [key: string]: any };

export interface AnalyticsEventInterface {
  name: string;
  properties: () => AnalyticsProperties;
}

export abstract class AnalyticsEvent implements AnalyticsEventInterface {
  abstract readonly name: string;

  properties(): { [key: string]: any } {
    const dict: { [key: string]: any } = {};
    Object.entries(this).forEach(([key, value]) => {
      if (key !== "name" && value !== null && value !== undefined) {
        if (value instanceof Map) {
          dict[toSnakeCase(key)] = JSON.stringify(Object.fromEntries(value));
        } else {
          dict[toSnakeCase(key)] = value;
        }
      }
    });
    return dict;
  }
}

function toSnakeCase(camel: string): string {
  return camel.replace(/([A-Z])/g, "_$1").toLowerCase();
}

export class AnalyticsEnabledEvent extends AnalyticsEvent {
  readonly name = "Analytics enabled";
}
export class ViewPracticeEvent extends AnalyticsEvent {
  readonly name = "View practice";
}
export class ViewAwardsEvent extends AnalyticsEvent {
  readonly name = "View awards";
}
export class ViewMyClassEvent extends AnalyticsEvent {
  readonly name = "View my class";
}
export class ViewStudentAssignmentsEvent extends AnalyticsEvent {
  readonly name = "View student assignments";
}
export class SeeMoreAssignmentsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap see more assignments button";
}
export class ViewFlashpackSubjectsEvent extends AnalyticsEvent {
  readonly name = "View flashpack subjects";
}

export class ViewUpgradeEvent extends AnalyticsEvent {
  readonly name = "View upgrade";

  constructor(public upgradeReason: string) {
    super();
  }
}
export class UpgradeButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap upgrade button";
}

export class ViewGrownupsHurdleEvent extends AnalyticsEvent {
  readonly name = "View grownups hurdle";
}
export class GrownupsHurdleInteractionEvent extends AnalyticsEvent {
  readonly name = "Grownups hurdle interaction";
}
export class GrownupsHurdleContinueButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap grownups hurdle continue button";
}
export class GrownupsHurdlePassedEvent extends AnalyticsEvent {
  readonly name = "Grownups hurdle passed";
  constructor(public attempts: number) {
    super();
  }
}

export class GrownupsHurdleFailedEvent extends AnalyticsEvent {
  readonly name = "Grownups hurdle failed";
  constructor(public isLockedOut: boolean, public didInteract: boolean) {
    super();
  }
}

export class ViewSignUpEvent extends AnalyticsEvent {
  readonly name = "View sign up";
}

export class ViewEmailConfirmationEvent extends AnalyticsEvent {
  readonly name = "View email confirmation";
}
export class EmailConfirmationEnterCodeEvent extends AnalyticsEvent {
  readonly name = "Entered email confirmation code";
}
export class EmailConfirmationButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap confirm email button";
}
export class EmailConfirmationRegenerateCodeButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap regenerate confirmation code button";
}
export class EmailConfirmationSuccessfulEvent extends AnalyticsEvent {
  readonly name = "Email confirmation successful";
}
export class EmailConfirmationFailedEvent extends AnalyticsEvent {
  readonly name = "Email confirmation failed";
}

export class FlashpackSubjectCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap flashpack subject cell";

  constructor(public flashpackType: number) {
    super();
  }
}
export class CreateCustomFlashpackCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap create custom flashpack cell";

  constructor(public flashpackType: number) {
    super();
  }
}
export class ViewFlashpackItemSelectionEvent extends AnalyticsEvent {
  readonly name = "View flashpack item selection";
}
export class FlashpackItemSelectionCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap flashpack item selection cell";
}
export class FlashpackItemSelectionCardTapEvent extends AnalyticsEvent {
  readonly name = "Tap flashpack item selection card";
}
export class LessonStartEditFlashpackButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson start edit flashpack button";

  constructor(public lessonId: number, public flashpackId: number) {
    super();
  }
}
export class EditFlashpackMenuSettingsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap edit flashpack menu settings button";

  constructor(public lessonId: number, public flashpackId: number) {
    super();
  }
}
export class EditFlashpackMenuRenameButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap edit flashpack menu rename button";

  constructor(public lessonId: number, public flashpackId: number) {
    super();
  }
}
export class EditFlashpackMenuDeleteButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap edit flashpack menu delete button";

  constructor(public lessonId: number, public flashpackId: number) {
    super();
  }
}
export class ViewFlashpackSettingsEvent extends AnalyticsEvent {
  readonly name = "View flashpack settings";

  constructor(public flashpackId: number) {
    super();
  }
}
export class FlashpackSettingsEditWordsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap flashpack settings edit words button";

  constructor(public flashpackType: number, public flashpackId: number) {
    super();
  }
}
export class FlashpackSettingsEnableRandomizationTapEvent extends AnalyticsEvent {
  readonly name = "Tap flashpack settings enable randomization";
}
export class FlashpackSettingsDisableRandomizationTapEvent extends AnalyticsEvent {
  readonly name = "Tap flashpack settings disable randomization";
}

export class TabTapEvent extends AnalyticsEvent {
  readonly name = "Tap tab";

  constructor(public type: string) {
    super();
  }
}
export class UnitCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap unit cell";

  constructor(public unitId: number, public unitTitle: string) {
    super();
  }
}
export class FlashpackUnitCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap flashpack unit cell";

  constructor(public unitId: number, public unitTitle: string) {
    super();
  }
}
export class GameUnitCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap game unit cell";

  constructor(public unitId: number, public unitTitle: string) {
    super();
  }
}
export class FeaturedUnitCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap featured unit cell";

  constructor(public unitId: number, public unitTitle: string) {
    super();
  }
}
export class RecentlyActiveUnitCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap recently active unit cell";

  constructor(public unitId: number, public unitTitle: string) {
    super();
  }
}

export class WeeklySpecialsCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap weekly specials cell";

  constructor(public unitId: number, public unitTitle: string) {
    super();
  }
}
export class SuggestedLessonCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap suggested lesson cell";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class MyClassLessonCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap my class lesson cell";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class StudentAssignedLessonCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap student assigned lesson cell";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class ViewUnitEvent extends AnalyticsEvent {
  readonly name = "View unit";

  constructor(public unitId: number, public unitTitle?: string) {
    super();
  }
}
export class UnitHeaderPlayNextLessonButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap unit header play next lesson button";

  constructor(
    public unitId: number,
    public unitTitle: string,
    public lessonId: number,
    public lessonTitle: string
  ) {
    super();
  }
}
export class LessonCellTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson cell";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class ViewLessonStartEvent extends AnalyticsEvent {
  readonly name = "View lesson start";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class ChangeLessonSpeedTapEvent extends AnalyticsEvent {
  readonly name = "Tap change lesson speed button";

  constructor(public speed: string) {
    super();
  }
}
export class ResumeLessonButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap resume lesson button";
}
export class PauseLessonButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap pause lesson button";
}
export class ReadyToContinueButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap ready to continue button";
}
export class StartLessonButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap start lesson button";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public isStudioProject: boolean,
    public unitId?: number
  ) {
    super();
  }
}
export class LessonMaterialsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson materials button";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class LessonMaterialsPostLessonButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson materials button (post lesson)";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class LessonStandardsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson standards button";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class AssignLessonButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap assign lesson button";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public unitId?: number
  ) {
    super();
  }
}
export class UnassignLessonButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap unassign lesson button";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public unitId?: number
  ) {
    super();
  }
}
export class ViewLessonEndEvent extends AnalyticsEvent {
  readonly name = "View lesson end";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class LessonEndFinishButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson end finish button";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class LessonEndPlayAgainButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson end play again button";

  constructor(public lessonId: number, public lessonTitle: string) {
    super();
  }
}
export class LessonEndPlayNextButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson end play next button";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public nextLessonId: number,
    public nextLessonTitle: string
  ) {
    super();
  }
}
export class LessonEndNextLessonImageTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson end next lesson image";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public nextLessonId: number,
    public nextLessonTitle: string
  ) {
    super();
  }
}
export class MenuSelectProfileButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap select profile menu button";

  constructor(public profileId: number) {
    super();
  }
}
export class MenuDashboardButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap dashboard menu button";
}
export class MenuLogOutButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap log out menu button";
}
export class MenuProfileSettingsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap profile settings menu button";
}
export class MenuAddProfileButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap add profile menu button";
}
export class MenuForParentsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap for parents menu button";
}
export class MenuUpgradeButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap upgrade menu button";
}
export class MenuLogInButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap log in menu button";
}
export class MenuLessonRecordingsButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap lesson recordings menu button";
}
export class MenuSendFeedbackButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap send feedback menu button";
}
export class ViewLogInEvent extends AnalyticsEvent {
  readonly name = "View log in";
}
export class ViewLogInWithOneTimeTokenEvent extends AnalyticsEvent {
  readonly name = "View log in with one time token";
}
export class LoginWithCleverButtonTapEvent extends AnalyticsEvent {
  readonly name = "Login with Clever button tap";
}
export class LoginWithCleverSuccessfulEvent extends AnalyticsEvent {
  readonly name = "Login with Clever successful";
}
export class LoginWithCleverFailedEvent extends AnalyticsEvent {
  readonly name = "Login with Clever failed";
}
export class LoginWithClassLinkButtonTapEvent extends AnalyticsEvent {
  readonly name = "Login with ClassLink button tap";
}
export class LoginWithClassLinkSuccessfulEvent extends AnalyticsEvent {
  readonly name = "Login with ClassLink successful";
}
export class LoginWithClassLinkFailedEvent extends AnalyticsEvent {
  readonly name = "Login with ClassLink failed";
}
export class LoginWithOneTimeTokenSuccessfulEvent extends AnalyticsEvent {
  readonly name = "Login with one time token successful";
}
export class LoginWithOneTimeTokenFailedEvent extends AnalyticsEvent {
  readonly name = "Login with one time token failed";
}
export class ViewEditProfileEvent extends AnalyticsEvent {
  readonly name = "View edit profile";
}
export class ViewPronunciationEvent extends AnalyticsEvent {
  readonly name = "View pronunciation";
}
export class ViewSettingsEvent extends AnalyticsEvent {
  readonly name = "View settings";
}
export class ViewTeacherResourcesEvent extends AnalyticsEvent {
  readonly name = "View teacher resources";
}
export class ViewLegalEvent extends AnalyticsEvent {
  readonly name = "View legal";
}
export class ViewManageAccountEvent extends AnalyticsEvent {
  readonly name = "View manage account";
}
export class ManageAccountDeleteAccountTapEvent extends AnalyticsEvent {
  readonly name = "Tap manage account delete account cell";
}
export class ManageAccountManageSubscriptionTapEvent extends AnalyticsEvent {
  readonly name = "Tap manage account manage subscription cell";
}
export class ManageAccountLogOutTapEvent extends AnalyticsEvent {
  readonly name = "Tap manage account log out cell";
}
export class ViewDataHandlingEvent extends AnalyticsEvent {
  readonly name = "View data handling";

  constructor(public showsTouConsent: boolean) {
    super();
  }
}
export class ViewPurchaseChoiceEvent extends AnalyticsEvent {
  readonly name = "View purchase choice";
}

const purchaseTypeFromProductId = (productId: number) =>
  productId === 1
    ? "full version monthly"
    : productId === 2
    ? "full version annual"
    : "" + productId;

export class ChoosePurchaseEvent extends AnalyticsEvent {
  readonly name = "Choose purchase";
  public type: string;

  constructor(productId: number) {
    super();
    this.type = purchaseTypeFromProductId(productId);
  }
}
export class PurchaseButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap purchase button";
}
export class RedeemOfferCodeButtonTapEvent extends AnalyticsEvent {
  readonly name = "Tap redeem offer code button";
}
export class StripeCheckoutStartedEvent extends AnalyticsEvent {
  readonly name = "Stripe checkout started";
}
export class PurchaseSuccessfulEvent extends AnalyticsEvent {
  readonly name = "Purchase successful";
  public type: string;

  constructor(productId: number) {
    super();
    this.type = purchaseTypeFromProductId(productId);
  }
}
export class PurchasePendingEvent extends AnalyticsEvent {
  readonly name = "Purchase result pending";
}
export class PurchaseFailedEvent extends AnalyticsEvent {
  readonly name = "Purchase failed";

  constructor(description: string) {
    super();
  }
}
export class FilterSelectEvent extends AnalyticsEvent {
  readonly name = "Select filter value";

  constructor(
    public filterName: string,
    public filterValue: string,
    public selectedFilters: Map<string, readonly string[]>
  ) {
    super();
  }
}
export class FilterDeselectEvent extends AnalyticsEvent {
  readonly name = "Deselect filter value";

  constructor(
    public filterName: string,
    public filterValue: string,
    public selectedFilters: Map<string, readonly string[]>
  ) {
    super();
  }
}
export class FilterDeselectAllEvent extends AnalyticsEvent {
  readonly name = "Deselect all filter values";

  constructor(
    public filterName: string,
    public selectedFilters: Map<string, readonly string[]>
  ) {
    super();
  }
}
export class WebContentLoadSuccessfulEvent extends AnalyticsEvent {
  readonly name = "Web content load successful";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public webContentUrl: string
  ) {
    super();
  }
}
export class WebContentLoadFailedEvent extends AnalyticsEvent {
  readonly name = "Web content load failed";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public webContentUrl: string
  ) {
    super();
  }
}
export class WebContentErrorReportedEvent extends AnalyticsEvent {
  readonly name = "Web content error reported";

  constructor(
    public lessonId: number,
    public lessonTitle: string,
    public webContentUrl: string,
    public errorMessage?: string
  ) {
    super();
  }
}
export class SchoolDashboardDistrictPageViewEvent extends AnalyticsEvent {
  readonly name = "View school dashboard district page";

  constructor(public tabName: string, public districtId: number) {
    super();
  }
}
export class SchoolDashboardSchoolPageViewEvent extends AnalyticsEvent {
  readonly name = "View school dashboard school page";

  constructor(
    public tabName: string,
    public districtId: number | null,
    public schoolId: number
  ) {
    super();
  }
}
export class SchoolDashboardTeacherPageViewEvent extends AnalyticsEvent {
  readonly name = "View school dashboard teacher page";

  constructor(
    public tabName: string,
    public districtId: number | null,
    public schoolId: number,
    public teacherId: number
  ) {
    super();
  }
}
export class SchoolDashboardLessonPageViewEvent extends AnalyticsEvent {
  readonly name = "View school dashboard lesson page";

  constructor(
    public tabName: string,
    public districtId: number | null,
    public schoolId: number,
    public teacherId: number,
    public lessonId: number,
    public customFlashpackId: number | null,
    public lessonTitle: string
  ) {
    super();
  }
}
export class SchoolDashboardLessonExecutionPageViewEvent extends AnalyticsEvent {
  readonly name = "View school dashboard lesson execution page";

  constructor(
    public tabName: string,
    public districtId: number | null,
    public schoolId: number,
    public teacherId: number,
    public studentUserId: number,
    public lessonId: number,
    public customFlashpackId: number | null,
    public lessonTitle: string,
    public lessonExecutionId: string
  ) {
    super();
  }
}
export class SchoolDashboardUserPageViewEvent extends AnalyticsEvent {
  readonly name = "View school dashboard user page";

  constructor(
    public tabName: string,
    public districtId: number | null,
    public schoolId: number,
    public teacherId: number,
    public studentUserId: number
  ) {
    super();
  }
}

export class ViewLessonRecordingsEvent extends AnalyticsEvent {
  readonly name = "View lesson recordings";
}

export class ViewLessonRecordingEvent extends AnalyticsEvent {
  readonly name = "View lesson recording";

  constructor(public recordingId: number) {
    super();
  }
}

export const AnalyticsContext = React.createContext<AnalyticsContextValue>(
  null!
);
